import {
  Box,
  Chip,
  chipClasses,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { IconCheckmark, IconWarning } from 'assets/img';
import { BulkShiftCreationCSVUploadResponse } from 'ogp/services/shift-service.types';
import { useTranslation } from 'react-i18next';

type Props = {
  results: BulkShiftCreationCSVUploadResponse;
};

export const ResultList = ({ results }: Props) => {
  const { t } = useTranslation('ogp-screen-specific');
  return (
    <StyledContainerBox>
      <StyledTitle variant="h3">{t('planning.bulk_shift_creation.results.title')}</StyledTitle>
      <StyledSuccessChip
        icon={<StyledCheckmarkIcon width={25} height={25} as={IconCheckmark} />}
        label={`${results.created.length} ${t('planning.bulk_shift_creation.results.shifts_created')}`}
      />
      <StyledErrorChip
        icon={<StyledWarningIcon width={20} height={20} as={IconWarning} />}
        label={`${results.failed.length} ${t('planning.bulk_shift_creation.results.shifts_failed')}`}
      />
      {results.failed.length > 0 && (
        <TableContainer>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell>{t('planning.bulk_shift_creation.results.table_header.row')}</TableCell>
                <TableCell>
                  {t('planning.bulk_shift_creation.results.table_header.reason')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.failed.map((result) => (
                <StyledTableRow key={result.rowNr}>
                  <TableCell>{result.rowNr}</TableCell>
                  <TableCell>{result.reason}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </StyledTable>
        </TableContainer>
      )}
    </StyledContainerBox>
  );
};

const StyledContainerBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(8),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StyledTable = styled(Table)(() => ({
  width: '100%',
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:last-child td, &:last-child th': { border: 0 },
}));

const StyledCheckmarkIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwSuccess[100],
    strokeWidth: theme.spacing(0.5),
  },
}));

const StyledWarningIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwDanger[100],
    strokeWidth: theme.spacing(0.5),
  },
}));

const StyledSuccessChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.lwSuccess[10],
  [`& .${chipClasses.label}`]: {
    color: theme.palette.lwSuccess[100],
  },
}));

const StyledErrorChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.lwDanger[10],
  [`& .${chipClasses.label}`]: {
    color: theme.palette.lwDanger[100],
  },
}));
